import { getUserCredentials, revokeSession } from "./user";
import { collection, type DocumentData, onSnapshot } from "firebase/firestore";
import { getFirestore } from "utilities/firebase";
import { getDeviceId } from "utilities/getDeviceId";
import { z, type infer as zInfer } from "zod";
import type { Nullable } from "@/types/utilities";
import type { ValidationError } from "features/auth/utils/authFetch";
import { submitVerificationAttemptPayloadSchema } from "features/account/types/Verification";
export type MessageError = ResultError | QueueError;

export const browserInfoSchema = z
  .object({
    browserJavascriptEnabled: z.boolean(),
    browserJavaEnabled: z.boolean(),
    browserColorDepth: z.string(),
    browserLanguage: z.string(),
    browserScreenHeight: z.string(),
    browserScreenWidth: z.string(),
    browserTZ: z.string(),
    browserUserAgent: z.string(),
  })
  .nullable();
export const payloadsSchema = z.object({
  "generate-apple-pay-session": z.object({
    validationUrl: z.string(),
  }),
  "update-password": z.object({
    password: z.string(),
    newPassword: z.string(),
  }),
  "generate-avatar-upload-url": z.null(),
  "complete-user-profile": z.object({
    phone: z.string(),
  }),
  "confirm-avatar-upload": z.object({
    asset_id: z.string(),
    public_id: z.string(),
    version: z.number(),
    version_id: z.string(),
    signature: z.string(),
    width: z.number(),
    height: z.number(),
    format: z.string(),
    resource_type: z.string(),
    created_at: z.string(),
    tags: z.array(z.string()),
    bytes: z.number(),
    type: z.string(),
    etag: z.string(),
    placeholder: z.boolean(),
    url: z.string(),
    secure_url: z.string(),
    folder: z.string(),
    access_mode: z.string(),
    overwritten: z.boolean(),
    original_filename: z.string(),
    api_key: z.string(),
  }),
  "confirm-avatar-delete": z.null(),
  "claim-promotion-code": z.object({
    code: z.string(),
  }),
  "claim-rakeback-balance": z.null(),
  "update-notification-preference": z.record(
    z.string(),
    z.union([z.string(), z.boolean()]),
  ),
  "update-email-subscription": z.object({}),
  "user-cooldown": z.object({
    days: z.number(),
  }),
  "self-exclude": z.object({
    months: z.number().nullable(),
    permanent: z.boolean(),
    closedReason: z.enum(["self_excluded", "user_requested"]),
  }),
  "close-account": z.object({}),
  "update-deposit-limits": z.object({
    maximumDailyDepositAmount: z.number(),
    dailyLimitDays: z.number(),
  }),
  "dismiss-all-notifications": z.null(),
  "dismiss-notification": z.object({
    id: z.string(),
  }),
  "update-user-settings": z.object({
    acceptOdds: z.string().nullable(),
    oddsFormat: z.string().nullable(),
    defaultHub: z.string().nullable(),
    allowCancelWithdrawal: z.boolean().nullable(),
  }),
  "reopen-account": z.object({
    maximumDailyDepositAmount: z.number().optional(),
    dailyLimitDays: z.number().optional(),
    responsibleGamblingMessageAccepted: z.boolean(),
    accountManagementToolsMessageAccepted: z.boolean(),
  }),
  "betting-submit-slip": z.record(z.string(), z.any()),
  "skrill-deposit": z.object({
    amount: z.number(),
    returnUrl: z.string(),
    returnUrlText: z.string(),
    cancelUrl: z.string(),
    logoUrl: z.string(),
  }),
  "gigadat-interac-deposit": z.object({
    amount: z.number(),
    provider: z.literal("GIGADAT_INTERAC"),
    verification: z.boolean(),
    promotionCode: z.string().optional(),
  }),
  "gigadat-interac-add-wallet": z.object({
    email: z.email(),
    mobile: z.string(),
  }),
  "gigadat-interac-remove-wallet": z.object({
    id: z.string(),
  }),
  "gigadat-interac-set-default-wallet": z.object({
    walletId: z.string(),
  }),
  "mw-deposit-funds-3ds": z.union([
    z.object({
      amount: z.number(),
      digitalWalletToken: z.string(),
      digitalWalletType: z.enum(["Google", "Apple"]).optional(),
      browserInfo: browserInfoSchema,
      panSuffix: z.string().optional(),
    }),
    z.object({
      amount: z.number(),
      csc: z.string().optional(),
      cardId: z.string(),
      browserInfo: browserInfoSchema,
    }),
  ]),
  "create-au-bank-account": z.object({
    bsb: z.string(),
    name: z.string(),
    number: z.string(),
  }),
  "cancel-withdrawal": z.string(),
  "create-withdrawal": z.union([
    z.object({
      amount: z.number(),
      bankAccountId: z.string(),
      currency: z.string(),
      externalType: z.literal("AUD_BANK_ACCOUNT"),
      userExternalWalletId: z.string().optional(),
      verificationRequestId: z.string().optional(),
    }),
    z.object({
      amount: z.number(),
      skrillEmail: z.string(),
      currency: z.string(),
      externalType: z.literal("SKRILL_ACCOUNT"),
    }),
    z.object({
      amount: z.number(),
      currency: z.string(),
      externalType: z.literal("GIGADAT_INTERAC"),
      userExternalWalletId: z.string().optional(),
    }),
  ]),
  "remove-bank-account": z.null(),
  "mw-get-add-card-access-token": z.object({
    numberFirst: z.string(),
    numberLast: z.string(),
    name: z.string(),
    expiryMonth: z.string(),
    expiryYear: z.string(),
    returnUrl: z.string(),
  }),
  "mw-confirm-card": z.object({
    cardId: z.string(),
  }),
  "verify-card": z.object({
    code: z.string(),
    id: z.string(),
  }),
  "mw-begin-verification": z.object({
    id: z.string(),
  }),
  "mw-remove-card": z.object({
    id: z.string(),
  }),
  "generate-user-pay-id": z.object({}),
  "get-deposit-limits": z.object({}),
  "validate-referral-code": z.object({
    code: z.string(),
    claimingOnSignup: z.boolean(),
  }),
  "pickems-submit-selection": z.object({
    contestId: z.string(),
    selections: z.any(),
    channel: z.string(),
  }),
  "get-bet-limits": z.object({}),
  "migrate-user-profile": z.object({}),
  "create-entry-share": z.object({
    shareId: z.string(),
  }),
  "accept-licence-terms": z.object({
    nationality: z.string(),
    accepted: z.boolean(),
    licence: z.enum(["NTRC", "ISLE_OF_MAN"]),
  }),
  "start-gaming-session": z.object({
    gameId: z.string(),
    desktop: z.boolean(),
  }),
  "end-gaming-session": z.object({
    token: z.string(),
  }),
  "end-all-gaming-sessions": z.null(),
  "cashout-bet": z.object({
    betId: z.string(),
    amount: z.number(),
  }),
  "update-phone-number": z.object({
    phone: z.string(),
    phoneVerificationRequestId: z.string(),
    emailVerificationRequestId: z.string(),
  }),
  "update-display-username": z.object({
    newDisplayUsername: z.string(),
  }),
  "create-verification-plan": z.object({
    permissions: z.string().array(),
  }),
  "submit-verification-attempt-data": submitVerificationAttemptPayloadSchema,
});

export type Payloads = zInfer<typeof payloadsSchema>;
type Module = keyof Payloads;

type Options = {
  basicAuth?: {
    username: string;
    password: string;
  };
  ignoreErrors?: boolean;
  numResponses?: number;
  timeOutMs?: number;
  // Fires per response as it streams in, before all numResponses arrive.
  onEachResponse?: (response: Response) => void;
};

export type Response = {
  code: string;
  createdAt: Date;
  data?: any; // TODO: have stricter API responses
  displayText?: string;
  status: "SUCCESS" | "ERROR";
  errors?: [];
};

export type Result = [Nullable<Response>, Nullable<MessageError>, string];
export type Results = [Nullable<Response[]>, Nullable<MessageError>, string];

const API_URI = process.env.GATSBY_API_URI;

class ResponseError extends Error {
  createdAt: Date;
  errors: ValidationError[];
  code: string;
  messageId?: string;

  constructor({
    createdAt,
    errors,
    code,
    displayText,
    messageId,
  }: {
    createdAt: Date;
    errors: ValidationError[];
    code: string;
    displayText?: string;
    messageId?: string;
  }) {
    super(displayText);
    this.createdAt = createdAt;
    this.errors = errors;
    this.code = code;
    this.messageId = messageId;
  }
}

export class ResultError extends ResponseError {}
export class QueueError extends ResponseError {}

const sendMessage = async function <M extends Module>(
  module: M,
  payload?: Payloads[M],
  options?: Options,
): Promise<[Nullable<Response>, Nullable<QueueError>]> {
  const getHeaders = () => {
    const { token } = getUserCredentials();

    const headers = {
      "Content-Type": "application/json",
      "X-DeviceId": getDeviceId(),
      Authorization: `Bearer ${token}`,
    };

    if (options?.basicAuth) {
      const { username, password } = options.basicAuth;
      headers["Authorization"] =
        `Bearer ${token} Basic ${btoa(`${username}:${password}`)}`;
    }

    return headers;
  };

  try {
    const response = await fetch(`${API_URI}/messages/${module}`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload ?? null),
    });

    const json = await response.json();

    if (response.ok) {
      return [
        {
          createdAt: new Date(),
          status: "SUCCESS",
          code: "queue-success",
          data: json,
        },
        null,
      ];
    }

    if (response.status === 401 || response.status === 403) {
      // User is not authorised, need to force logout
      await revokeSession();
    }

    // if we reached this point, something errored out, and we need to throw the error
    throw json;
  } catch (error: any) {
    return [
      null,
      new QueueError({
        createdAt: new Date(),
        errors: error.errors || [],
        code: "queue-error",
        displayText:
          error.errors?.[0]?.message ||
          `Infrastructure error when queuing message: ${error.message}`,
      }),
    ];
  }
};

const message = async function <M extends Module>(
  module: M,
  payload?: Payloads[M],
  options?: Omit<Options, "numResponses">,
): Promise<Result> {
  const [responses, error, messageId] = await messages(module, payload, {
    ...options,
    numResponses: 1,
  });

  if (error) {
    return [null, error, messageId];
  }

  return [responses ? responses[0] : null, null, messageId];
};

const messages = async function <M extends Module>(
  module: M,
  payload?: Payloads[M],
  options?: Options,
): Promise<Results> {
  const [messageResponse, messageError] = await sendMessage(
    module,
    payload,
    options,
  );

  const messageId = messageResponse?.data?.messageId;

  if (messageError) {
    return [null, messageError, messageId];
  }

  return messagesById(messageId, options);
};

const messagesById = async (
  messageId: string,
  options?: Options,
): Promise<Results> => {
  const generator = messageGenerator(messageId, options?.timeOutMs || 20000);

  const numResponse = options?.numResponses || 1;

  // Keep all responses that we get from firestore till we get to number of responses we want
  const responses = [];

  const ignoreErrors = options?.ignoreErrors || false;

  try {
    for await (const response of generator) {
      if (response.status === "ERROR" && !ignoreErrors) {
        return [
          null,
          new ResultError({
            createdAt: response.createdAt,
            errors: response.errors || [],
            code: response.code,
            displayText: response.displayText,
            messageId,
          }),
          messageId,
        ];
      }

      responses.push(response);
      options?.onEachResponse?.(response);

      if (responses.length === numResponse) {
        return [responses, null, messageId];
      }
    }
  } catch (error: any) {
    return [
      null,
      new ResultError({
        createdAt: new Date(),
        errors: [error.message],
        code: "message-timeout",
        displayText: "Timed out waiting for a response",
        messageId,
      }),
      messageId,
    ];
  }

  // we received no response, return empty response
  return [null, null, messageId];
};

const watch = async function <M extends Module>(
  module: M,
  payload?: Payloads[M],
  options?: Options,
): Promise<
  [
    Nullable<{
      next: () => Promise<Result>;
      messageId: string;
    }>,
    Nullable<QueueError>,
  ]
> {
  const [messageResponse, messageError] = await sendMessage(
    module,
    payload,
    options,
  );

  if (messageError || !messageResponse) {
    return [null, messageError];
  }

  return [
    {
      next: createNextWatcher(messageResponse.data.messageId, options),
      messageId: messageResponse.data.messageId,
    },
    null,
  ] as const;
};

const createNextWatcher = (
  messageId: string,
  options?: Options,
): (() => Promise<Result>) => {
  const generator = messageGenerator(messageId, options?.timeOutMs || 20000);

  return async () => {
    try {
      const { value: response, done } = await generator.next();

      if (done || !response) {
        return [null, null, messageId];
      }

      if (response.status === "ERROR") {
        return [
          null,
          new ResultError({
            createdAt: response.createdAt,
            errors: response.errors || [],
            code: response.code,
            displayText: response.displayText,
            messageId,
          }),
          messageId,
        ];
      }

      return [response, null, messageId];
    } catch {
      return [
        null,
        new ResultError({
          createdAt: new Date(),
          code: "message-timeout",
          displayText: "Timed out waiting for a response",
          errors: [],
          messageId,
        }),
        messageId,
      ];
    }
  };
};

async function* messageGenerator(
  messageId: string,
  timeOutMs = 20000, // defaults to 20 seconds
): AsyncGenerator<Response, void> {
  const { userId } = getUserCredentials();

  const messagesRef = collection(
    getFirestore(),
    "users",
    userId,
    "messageResponses",
    messageId,
    "responses",
  );

  const yieldedDocs = new Set<string>();

  // we will store documents that are not yielded yet in a queue
  const queue: DocumentData = [];

  let resolvePromise: (() => void) | null = null;

  const unsubscribe = onSnapshot(messagesRef, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === "added" && !yieldedDocs.has(change.doc.id)) {
        yieldedDocs.add(change.doc.id);
        const data = change.doc.data();
        queue.push({
          ...data,
          // convert timestamp to an actual date object
          createdAt: new Date(data.createdAt),
        });
      }

      // Resolve the promise if there are items in the queue
      if (resolvePromise && queue.length > 0) {
        resolvePromise();
      }
    });
  });

  try {
    while (true) {
      // If the queue is empty, wait for new items
      if (queue.length === 0) {
        await Promise.race([
          new Promise<void>((resolve) => (resolvePromise = resolve)),
          // we timeout response after 20s
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error("Timed out waiting for response")),
              timeOutMs,
            ),
          ),
        ]);
      }

      // Yield items from the queue one by one
      while (queue.length > 0) {
        yield queue.shift()!;
      }
    }
  } finally {
    // once loop is finished or generator is closed, unsubscribe from the snapshot
    unsubscribe();
  }
}

export { message, watch, messages, messagesById };
